import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { PatternFormat } from 'react-number-format';
import { isDesktop } from 'react-device-detect';
import Link from 'next/link';

import { config } from '_config';
import { theme } from '_constants/theme';
import { ROUTES } from '_constants/routes';
import { ICONS_ALIASES } from '_constants/icons';
import { FIELDS_KEYS } from '_constants/forms';

import { refreshActiveProductCode } from '_redux/products/slice';
import { refreshForm, refreshProducts } from '_redux/order/slice';
import { selectActiveProductCode } from '_redux/products/selector';
import { selectFormValues as selectFilingFormValues } from '_redux/order/selector';
import { selectGeolocation } from '_redux/general/selector';

import Icon from '_components/atomic/Icon';
import Text from '_components/atomic/layouts/Card/Text';
import StateAndEntitySelectForm2 from '_components/composed/forms/StateAndEntitySelectForm2';

import { transformRowsToIncludedProducts } from '../PackageSelectSection/helpers';

const TrustPilotSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={80}
    height={20}
    viewBox="0 0 101 25"
    fill="none"
  >
    <path
      d="M13.0942 17.9551L18.4183 16.6313L20.6427 23.3576L13.0942 17.9551ZM25.3469 9.26091H15.975L13.0942 0.602539L10.2133 9.26091H0.841431L8.42646 14.6277L5.5456 23.286L13.1306 17.9193L17.7983 14.6277L25.3469 9.26091Z"
      fill="#00B67A"
    />
    <path
      d="M33.1623 9.29995V21.3375H31.1036V9.29995H33.1623ZM36.9406 9.29995V10.9535H27.3584V9.29995H36.9406ZM39.735 14.0951V21.3375H37.7425V12.392H39.6441L39.735 14.0951ZM42.4716 12.3341L42.455 14.1861C42.3338 14.164 42.2015 14.1475 42.0582 14.1365C41.9204 14.1255 41.7826 14.1199 41.6448 14.1199C41.3031 14.1199 41.0027 14.1695 40.7436 14.2688C40.4846 14.3625 40.2669 14.5003 40.0905 14.6821C39.9196 14.8585 39.7874 15.0735 39.6937 15.327C39.6 15.5805 39.5448 15.8644 39.5283 16.1786L39.0736 16.2116C39.0736 15.6494 39.1287 15.1286 39.2389 14.6491C39.3492 14.1695 39.5145 13.7479 39.735 13.3841C39.961 13.0204 40.2421 12.7365 40.5783 12.5326C40.92 12.3286 41.3141 12.2267 41.7606 12.2267C41.8818 12.2267 42.0113 12.2377 42.1491 12.2597C42.2924 12.2818 42.3999 12.3066 42.4716 12.3341ZM49.1022 19.2293V12.392H51.1029V21.3375H49.2179L49.1022 19.2293ZM49.3833 17.3691L50.0529 17.3526C50.0529 17.9533 49.9868 18.5073 49.8545 19.0144C49.7222 19.5159 49.5183 19.9541 49.2427 20.3289C48.9671 20.6982 48.6144 20.9875 48.1845 21.197C47.7545 21.4009 47.2392 21.5029 46.6384 21.5029C46.203 21.5029 45.8034 21.4395 45.4396 21.3127C45.0758 21.186 44.7617 20.9903 44.4971 20.7257C44.2381 20.4612 44.0369 20.1167 43.8936 19.6923C43.7503 19.2679 43.6786 18.7608 43.6786 18.1711V12.392H45.6711V18.1876C45.6711 18.5128 45.7097 18.7856 45.7869 19.0061C45.864 19.221 45.9687 19.3947 46.101 19.5269C46.2333 19.6592 46.3876 19.7529 46.564 19.808C46.7404 19.8632 46.9278 19.8907 47.1262 19.8907C47.6939 19.8907 48.1404 19.7805 48.4656 19.56C48.7963 19.334 49.0305 19.0309 49.1683 18.6506C49.3116 18.2703 49.3833 17.8431 49.3833 17.3691ZM58.1634 18.9151C58.1634 18.7167 58.1138 18.5376 58.0146 18.3777C57.9154 18.2124 57.7252 18.0636 57.4441 17.9313C57.1686 17.799 56.7607 17.6778 56.2205 17.5675C55.7465 17.4628 55.3111 17.3388 54.9143 17.1955C54.5229 17.0467 54.1867 16.8675 53.9056 16.6581C53.6245 16.4486 53.4068 16.2006 53.2525 15.914C53.0982 15.6274 53.021 15.2967 53.021 14.9219C53.021 14.5581 53.1009 14.2136 53.2608 13.8885C53.4206 13.5633 53.6493 13.2767 53.947 13.0286C54.2446 12.7806 54.6056 12.5849 55.03 12.4416C55.4599 12.2983 55.9394 12.2267 56.4686 12.2267C57.2182 12.2267 57.8603 12.3534 58.3949 12.607C58.9351 12.855 59.3484 13.194 59.6351 13.6239C59.9217 14.0483 60.065 14.5278 60.065 15.0624H58.0725C58.0725 14.8254 58.0119 14.605 57.8906 14.401C57.7748 14.1916 57.5985 14.0235 57.3615 13.8967C57.1245 13.7644 56.8268 13.6983 56.4686 13.6983C56.1268 13.6983 55.843 13.7534 55.617 13.8636C55.3965 13.9684 55.2312 14.1062 55.121 14.277C55.0162 14.4479 54.9639 14.6353 54.9639 14.8392C54.9639 14.988 54.9914 15.1231 55.0465 15.2443C55.1072 15.3601 55.2064 15.4676 55.3442 15.5668C55.482 15.6605 55.6694 15.7487 55.9064 15.8313C56.1489 15.914 56.452 15.9939 56.8158 16.0711C57.4993 16.2144 58.0863 16.399 58.5768 16.625C59.0729 16.8455 59.4532 17.1321 59.7177 17.4848C59.9823 17.8321 60.1146 18.273 60.1146 18.8077C60.1146 19.2045 60.0291 19.5683 59.8583 19.899C59.6929 20.2242 59.4504 20.508 59.1307 20.7505C58.8111 20.9875 58.428 21.1722 57.9815 21.3045C57.5406 21.4368 57.0445 21.5029 56.4934 21.5029C55.6832 21.5029 54.9969 21.3596 54.4347 21.073C53.8726 20.7809 53.4454 20.4088 53.1533 19.9569C52.8667 19.4994 52.7234 19.0254 52.7234 18.5348H54.6497C54.6718 18.9041 54.7737 19.199 54.9556 19.4195C55.143 19.6344 55.3745 19.7915 55.6501 19.8907C55.9312 19.9844 56.2205 20.0313 56.5182 20.0313C56.8764 20.0313 57.1768 19.9844 57.4193 19.8907C57.6619 19.7915 57.8465 19.6592 57.9733 19.4939C58.1 19.323 58.1634 19.1301 58.1634 18.9151ZM65.9598 12.392V13.8471H60.9165V12.392H65.9598ZM62.3716 10.2011H64.3641V18.8655C64.3641 19.1411 64.4027 19.3533 64.4799 19.5021C64.5625 19.6454 64.6755 19.7419 64.8188 19.7915C64.9621 19.8411 65.1302 19.8659 65.3231 19.8659C65.4609 19.8659 65.5932 19.8576 65.72 19.8411C65.8468 19.8246 65.9487 19.808 66.0259 19.7915L66.0342 21.3127C65.8688 21.3623 65.6759 21.4064 65.4554 21.445C65.2405 21.4836 64.9924 21.5029 64.7113 21.5029C64.2539 21.5029 63.8488 21.423 63.496 21.2631C63.1433 21.0978 62.8677 20.8305 62.6693 20.4612C62.4708 20.0919 62.3716 19.6013 62.3716 18.9895V10.2011ZM69.4983 14.1117V24.7769H67.5058V12.392H69.3412L69.4983 14.1117ZM75.3269 16.7821V16.9557C75.3269 17.6061 75.2498 18.2096 75.0954 18.7663C74.9466 19.3175 74.7234 19.7998 74.4257 20.2131C74.1336 20.621 73.7726 20.9379 73.3427 21.1639C72.9128 21.3899 72.4167 21.5029 71.8545 21.5029C71.2978 21.5029 70.8101 21.4009 70.3912 21.197C69.9778 20.9875 69.6278 20.6927 69.3412 20.3124C69.0546 19.9321 68.8231 19.4856 68.6467 18.973C68.4758 18.4549 68.3546 17.8872 68.2829 17.2699V16.6002C68.3546 15.9443 68.4758 15.3491 68.6467 14.8144C68.8231 14.2798 69.0546 13.8196 69.3412 13.4337C69.6278 13.0479 69.9778 12.7503 70.3912 12.5408C70.8046 12.3314 71.2868 12.2267 71.838 12.2267C72.4002 12.2267 72.899 12.3369 73.3344 12.5574C73.7699 12.7723 74.1364 13.081 74.434 13.4833C74.7316 13.8802 74.9549 14.3597 75.1037 14.9219C75.2525 15.4786 75.3269 16.0987 75.3269 16.7821ZM73.3344 16.9557V16.7821C73.3344 16.3687 73.2958 15.9857 73.2187 15.6329C73.1415 15.2746 73.0203 14.9605 72.8549 14.6904C72.6896 14.4203 72.4774 14.2109 72.2183 14.0621C71.9648 13.9077 71.6589 13.8306 71.3006 13.8306C70.9479 13.8306 70.6447 13.8912 70.3912 14.0125C70.1376 14.1282 69.9254 14.2908 69.7546 14.5003C69.5837 14.7097 69.4514 14.955 69.3577 15.2361C69.264 15.5117 69.1979 15.812 69.1593 16.1372V17.7411C69.2254 18.138 69.3384 18.5018 69.4983 18.8325C69.6581 19.1632 69.8841 19.4277 70.1762 19.6262C70.4738 19.8191 70.8542 19.9155 71.3171 19.9155C71.6754 19.9155 71.9813 19.8384 72.2348 19.684C72.4884 19.5297 72.6951 19.3175 72.8549 19.0474C73.0203 18.7718 73.1415 18.4549 73.2187 18.0966C73.2958 17.7384 73.3344 17.3581 73.3344 16.9557ZM79.1548 12.392V21.3375H77.1541V12.392H79.1548ZM77.0218 10.044C77.0218 9.74088 77.121 9.4901 77.3194 9.29168C77.5233 9.08775 77.8044 8.98578 78.1627 8.98578C78.5154 8.98578 78.7938 9.08775 78.9977 9.29168C79.2017 9.4901 79.3036 9.74088 79.3036 10.044C79.3036 10.3417 79.2017 10.5897 78.9977 10.7881C78.7938 10.9865 78.5154 11.0857 78.1627 11.0857C77.8044 11.0857 77.5233 10.9865 77.3194 10.7881C77.121 10.5897 77.0218 10.3417 77.0218 10.044ZM83.4705 8.63854V21.3375H81.4697V8.63854H83.4705ZM85.2728 16.964V16.7738C85.2728 16.129 85.3665 15.5309 85.5539 14.9798C85.7413 14.4231 86.0114 13.9408 86.3641 13.5329C86.7224 13.1196 87.1578 12.7999 87.6704 12.5739C88.1885 12.3424 88.7728 12.2267 89.4231 12.2267C90.079 12.2267 90.6633 12.3424 91.1759 12.5739C91.694 12.7999 92.1321 13.1196 92.4904 13.5329C92.8487 13.9408 93.1215 14.4231 93.3089 14.9798C93.4963 15.5309 93.59 16.129 93.59 16.7738V16.964C93.59 17.6089 93.4963 18.2069 93.3089 18.7581C93.1215 19.3092 92.8487 19.7915 92.4904 20.2049C92.1321 20.6127 91.6967 20.9324 91.1841 21.1639C90.6715 21.3899 90.0901 21.5029 89.4397 21.5029C88.7838 21.5029 88.1968 21.3899 87.6787 21.1639C87.1661 20.9324 86.7307 20.6127 86.3724 20.2049C86.0141 19.7915 85.7413 19.3092 85.5539 18.7581C85.3665 18.2069 85.2728 17.6089 85.2728 16.964ZM87.2653 16.7738V16.964C87.2653 17.3663 87.3066 17.7467 87.3893 18.1049C87.472 18.4632 87.6015 18.7773 87.7779 19.0474C87.9543 19.3175 88.1802 19.5297 88.4558 19.684C88.7314 19.8384 89.0594 19.9155 89.4397 19.9155C89.809 19.9155 90.1286 19.8384 90.3987 19.684C90.6743 19.5297 90.9003 19.3175 91.0767 19.0474C91.253 18.7773 91.3826 18.4632 91.4652 18.1049C91.5534 17.7467 91.5975 17.3663 91.5975 16.964V16.7738C91.5975 16.377 91.5534 16.0022 91.4652 15.6494C91.3826 15.2912 91.2503 14.9743 91.0684 14.6987C90.892 14.4231 90.666 14.2081 90.3904 14.0538C90.1204 13.894 89.7979 13.814 89.4231 13.814C89.0483 13.814 88.7231 13.894 88.4476 14.0538C88.1775 14.2081 87.9543 14.4231 87.7779 14.6987C87.6015 14.9743 87.472 15.2912 87.3893 15.6494C87.3066 16.0022 87.2653 16.377 87.2653 16.7738ZM99.3608 12.392V13.8471H94.3175V12.392H99.3608ZM95.7726 10.2011H97.7651V18.8655C97.7651 19.1411 97.8037 19.3533 97.8809 19.5021C97.9635 19.6454 98.0765 19.7419 98.2198 19.7915C98.3631 19.8411 98.5312 19.8659 98.7242 19.8659C98.862 19.8659 98.9942 19.8576 99.121 19.8411C99.2478 19.8246 99.3497 19.808 99.4269 19.7915L99.4352 21.3127C99.2698 21.3623 99.0769 21.4064 98.8564 21.445C98.6415 21.4836 98.3935 21.5029 98.1124 21.5029C97.6549 21.5029 97.2498 21.423 96.897 21.2631C96.5443 21.0978 96.2687 20.8305 96.0703 20.4612C95.8718 20.0919 95.7726 19.6013 95.7726 18.9895V10.2011Z"
      fill="black"
    />
  </svg>
);

const ArrowSvg = () => (
  <svg
    width={15}
    height={13}
    viewBox="0 0 15 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.4995 0.999842C2.4995 0.999842 5.5 11 13.8811 12.4117M0.998878 4.49975L1.73406 1.9361C1.90171 1.24608 2.57204 0.790159 3.30016 0.875315L6.48982 2.49972"
      stroke="#444CE7"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></path>
  </svg>
);

const StyledOuterContainer = styled.div`
  max-width: ${theme.spaces.large23X * 10 + 10 + 8}px;
  width: 100%;
  padding: ${theme.spaces.small2X}px;
`;

const StyledInnerTopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${theme.spaces.small}px;
  padding-bottom: ${theme.spaces.small}px;
  margin-bottom: ${theme.spaces.smallX}px;

  @media ${theme.devices.tablet} {
  }
`;

const StyledInnerBottomContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 438px;
  padding-top: ${theme.spaces.large4X}px;
  padding-bottom: ${theme.spaces.large4X}px;
  padding-left: ${theme.spaces.large}px;
  padding-right: ${theme.spaces.large}px;
  border-radius: 24px;
  border: 1px solid ${theme.colors.gray[200]};
  background-image: ${`url(${config.basePath}/images/v4-bg-left-element.svg),
    url(${config.basePath}/images/v4-bg-right-element.svg),
    linear-gradient(148.41deg, #ffffff 11.23%, #fafafa 80.96%)`};
  background-repeat: no-repeat;
  background-position: left 55px, right 55px, top left;
  background-size: auto 100%, auto 100%, cover;
  background-color: ${theme.colors.gray[100]};

  @media ${theme.devices.tablet} {
    background-position: left bottom, right bottom, top left;
    background-size: auto 30%, auto 30%, cover;
  }
`;

const StyledLogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: '185px';
  }

  @media ${theme.devices.tablet} {
    padding-left: ${theme.spaces.large2X}px;
  }
`;

const StyledContactLink = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
  padding-top: ${theme.spaces.smallX}px;
  padding-bottom: ${theme.spaces.smallX}px;
  padding-left: ${theme.spaces.medium}px;
  padding-right: ${theme.spaces.medium}px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.gray[200]};
  text-decoration: none;

  @media ${theme.devices.tablet} {
    display: none;
  }
`;

const StyledReviewsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
  min-height: 42px;
  padding-top: ${theme.spaces.smallX}px;
  padding-bottom: ${theme.spaces.smallX}px;
  padding-left: ${theme.spaces.medium}px;
  padding-right: ${theme.spaces.medium}px;
  margin-bottom: ${theme.spaces.large4X}px;
  border-radius: 24px;
  border: 1px solid ${theme.colors.gray[100]};
  box-shadow: 0 0 #0000, 0 0 #0000, 0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px -1px rgba(0, 0, 0, 0.1);
`;

const StyledFormContainer = styled.div`
  max-width: 740px;
  width: 100%;
`;

const StyledArrowIconContainer = styled.span`
  display: inline-block;
  margin-right: ${theme.spaces.regular}px;
`;

const StateAndEntitySelectSection2 = ({ submitRef }) => {
  const dispatch = useDispatch();
  const geolocation = useSelector(selectGeolocation);
  const activeProductCode = useSelector(selectActiveProductCode);
  const filingFormValues = useSelector(selectFilingFormValues);

  const handleSubmit = useCallback(
    (values) => {
      if (values) {
        if (
          values[FIELDS_KEYS.entity] &&
          values[FIELDS_KEYS.entity] !== activeProductCode
        ) {
          dispatch(refreshActiveProductCode(values[FIELDS_KEYS.entity]));
          dispatch(refreshForm({ ...values }));
          dispatch(
            refreshProducts([
              values[FIELDS_KEYS.entity],
              ...transformRowsToIncludedProducts({
                activeProcessingCode:
                  filingFormValues[FIELDS_KEYS.processingOption],
              }),
            ])
          );
        }

        if (values[FIELDS_KEYS.entity]) {
          if (
            values[FIELDS_KEYS.organizedState] &&
            values[FIELDS_KEYS.organizedState] !==
              filingFormValues[FIELDS_KEYS.organizedState]
          ) {
            dispatch(
              refreshForm({
                [FIELDS_KEYS.organizedState]:
                  values[FIELDS_KEYS.organizedState],
              })
            );
          }
        }
      }
    },
    [dispatch, activeProductCode, filingFormValues]
  );

  return (
    <StyledOuterContainer>
      <StyledInnerTopContainer>
        <Link href={ROUTES.home}>
          <StyledLogoContainer>
            <picture>
              <source
                srcSet={`${config.basePath}/images/logo.svg`}
                type="image/svg+xml"
              />
              <img
                src={`${config.basePath}/images/logo.svg`}
                title="Incorpified Logo"
                alt="Incorpified Logo"
              />
            </picture>
          </StyledLogoContainer>
        </Link>
        <StyledContactLink href={`tel:${config.phone}`}>
          <Icon alias={ICONS_ALIASES.phone} color={theme.colors.gray[700]} />
          <Text ignoreSpan size={theme.fontSizes.small} weight={700}>
            <PatternFormat
              value={config.phone?.substring(2)}
              displayType="text"
              format="(###) ###-####"
            />
          </Text>
        </StyledContactLink>
      </StyledInnerTopContainer>
      <StyledInnerBottomContainer>
        <StyledReviewsContainer>
          <Text
            size={theme.fontSizes.small}
            lineHeightRatio={1}
            topSpace={theme.spaces.smallest + 1}
          >
            {isDesktop && <b>Excellent&nbsp;</b>}
            <b>4.7</b> out of 5
          </Text>
          <TrustPilotSvg />
        </StyledReviewsContainer>
        <Text
          asTitle
          size={theme.fontSizes.large10X - 2}
          weight={700}
          lineHeightRatio={1}
          spanColor={theme.colors.indigo[600]}
          bottomSpace={theme.spaces.large}
          centered
          desktop
        >
          START YOUR BUSINESS
          <br />
          WITH <span>CONFIDENCE</span>
        </Text>
        <Text
          asTitle
          size={theme.fontSizes.large5X}
          weight={700}
          lineHeightRatio={1}
          spanColor={theme.colors.indigo[600]}
          bottomSpace={theme.spaces.large}
          centered
          mobile
        >
          START YOUR BUSINESS WITH <span>CONFIDENCE</span>
        </Text>
        <Text
          size={theme.fontSizes.medium}
          weight={400}
          bottomSpace={theme.spaces.large4X}
          centered
          desktop
        >
          Join over 1,000,000 happy business owners. Get started by choosing
          <br />
          your entity type and state of formation.
        </Text>
        <Text
          size={theme.fontSizes.medium}
          weight={400}
          bottomSpace={theme.spaces.large4X}
          centered
          mobile
        >
          Join over 1,000,000 happy business owners. Get started by choosing
          your entity type and state of formation.
        </Text>
        <StyledFormContainer>
          <StateAndEntitySelectForm2
            submitRef={submitRef}
            geolocation={geolocation}
            defaultValues={{
              [FIELDS_KEYS.entity]: activeProductCode,
              [FIELDS_KEYS.organizedState]:
                filingFormValues[FIELDS_KEYS.organizedState],
            }}
            onSubmit={handleSubmit}
          />
        </StyledFormContainer>
        {!activeProductCode &&
          !filingFormValues[FIELDS_KEYS.organizedState] && (
            <Text
              size={theme.fontSizes.smallX}
              topSpace={theme.spaces.smallest}
              bottomSpace={theme.spaces.large4X}
              centered
            >
              {isDesktop && (
                <StyledArrowIconContainer>
                  <ArrowSvg />
                </StyledArrowIconContainer>
              )}
              State-specific pricing will display below once your selection has
              been made.
            </Text>
          )}
      </StyledInnerBottomContainer>
    </StyledOuterContainer>
  );
};

StateAndEntitySelectSection2.propTypes = {
  submitRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
};

export default StateAndEntitySelectSection2;
